import { countsLine } from '@boViews/shared/clientCounts';
import { faArrowUpRightFromSquare, faLaptop } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Action from '@sharedComponents/Action';
import FlexBox from '@sharedComponents/FlexBox';
import Typography from '@sharedComponents/Typography';
import { useTranslation } from 'react-i18next';

import { targetDescription } from '../overrideRules';

import * as style from './style.module.css';

/**
 * Label of the target of an override row: the name of its client, followed by the name of the parent for a
 * sub-client, then a secondary line, the counts of the client (see countsLine). On a client row, the name links
 * to the page of the client; on a platform row, the secondary line starts with a laptop and the url of the
 * platform replaces the platforms and links to the page of the platform. The links, underlined, open in a new
 * tab, which an icon after their text tells. `children` follow the name.
 */
const TargetLabel = ({ row, children = null }) => {
    const { t } = useTranslation('bo');
    const description = targetDescription(row);

    const link = (path, name, content) => {
        const label = t('Open the page of {{name}}', { name });
        return (
            <Action action={`${import.meta.env.BASE_URL}${path}`} target="_blank" title={label} aria-label={label} className={style.link}>
                {content}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={style.externalIcon} aria-hidden="true" />
            </Action>
        );
    };

    const name = (
        <Typography span fontSize="s" fontWeight="medium" color="neutral-primary" className={style.name}>
            {description.name}
        </Typography>
    );

    return (
        <FlexBox flexDirection="column" gap="xxs" className={style.label}>
            <FlexBox alignItems="center" gap="xs" flexWrap="wrap">
                <Typography span fontSize="s">
                    {row.platform ? name : link(`clients/${row.client.uuid}`, description.name, name)}
                    {description.parentName && (
                        <Typography span fontSize="s" color="neutral-secondary">
                            {` (${description.parentName})`}
                        </Typography>
                    )}
                </Typography>
                {children}
            </FlexBox>
            <Typography fontSize="xs" color="neutral-tertiary">
                {row.platform ? (
                    <>
                        <FontAwesomeIcon icon={faLaptop} className={style.platformIcon} />
                        {description.counts.reference && `${description.counts.reference} · `}
                        {link(
                            `clients/${row.client.uuid}/platforms/${row.platform.uuid}`,
                            description.url,
                            <Typography span fontSize="xs" color="neutral-tertiary" className={style.name}>
                                {description.url}
                            </Typography>,
                        )}
                    </>
                ) : (
                    countsLine(t, description.counts)
                )}
            </Typography>
        </FlexBox>
    );
};

export default TargetLabel;
